import { NgRedux } from '@ptl/angular-redux-store';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StateStructure } from '@ptl/state';
import { DrugItem } from '../../../shared/interfaces/drug-item.interface';
import { DRXDosageResponse, DRXMemberSearchPayload, DRXMemberSearchResponse, DRXPharmacyResponse, DRXSessionStartResponse } from '../../../shared/interfaces/drx.interface';
import { DateService } from '../../../shared/services/date.service';
import { ReducerUtilityService } from '../../../shared/utility-services/reducer-utility.service';
import { plansNeedRefreshedFromBackend } from '../../plan-selection/configs/reducers/plans.reducer';
import { ZipCountyStateStructure } from '../../shared/interfaces/zipCountyStateStructure.interface';
import { GenericAndBrandObject, addMedicationToSwapMap, storeCurrentMedications, SelectedMedication, setSelectedMedications } from '../medication-information/reducers/medication.reducer';
import { DrugLookupService } from '../medication-information/services/drug-lookup.service';
import { DrxService } from '../medication-information/services/drx.service';
import { PharmacyState, PHARMACY_REDUX_STATE_ID, setPharmacyState } from '../pharmacy-information/configs/reducers/pharmacy.reducer';

@Component({
    selector: 'app-member-drug-information',
    templateUrl: './member-drug-information.component.html',
    styleUrls: ['./member-drug-information.component.scss'],
    standalone: false
})
export class MemberDrugInformationComponent implements OnInit {
  @Input() title = 'Member Lookup';
  @Input() subTitle = '(Optional)';
  @Input() description = 'Search for member to autofill medication and provider information.';
  @Input() memberSelectedLink = 'add-prescriptions';

  disableSubmit: boolean;
  showSearchSpinner: boolean = false;
  showSelectSpinner: any = {};
  showDirtyError: boolean = false;
  disableOtherSelect: boolean = false;

  form: UntypedFormGroup;

  memberSessionId: string;
  memberResults: DRXMemberSearchResponse[];
  showNoResults: Boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private ngRedux: NgRedux<StateStructure>,
    private drxService: DrxService,
    private drugLookupService: DrugLookupService,
    private dateService: DateService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.fb.group({
      firstName: this.fb.control(''),
      lastName: this.fb.control(''),
      dateOfBirth: this.fb.control('')
    });
  }

  async ngOnInit() {
    let response = await this.drxService.createMemberSession();

    if (response?.length) {
      this.memberSessionId = response[0].SessionID;
    }
    else {
      // Cannot use this page
    }
  }

  searchMembers() {
    if (!this.isFormValid()) {
      this.showDirtyError = true;
      return;
    }

    this.showNoResults = false;
    this.showDirtyError = false;
    this.showSearchSpinner = true;
    this.drxService.searchMember(this.memberSessionId, this.getSearchPayload()).then((response) => {
      this.memberResults = response;

      if (!this.memberResults?.length) {
        this.showNoResults = true;
      }
    })
      .finally(() => this.showSearchSpinner = false);
  }

  // This service cannot receive parameters that are empty
  // this function combines only the fields that were populated
  getSearchPayload(): DRXMemberSearchPayload {
    let formValue = this.form.value;
    let searchPayload = {} as DRXMemberSearchPayload;

    if (formValue.firstName) {
      searchPayload.firstName = formValue.firstName;
    }
    if (formValue.lastName) {
      searchPayload.lastName = formValue.lastName;
    }
    if (formValue.dateOfBirth) {
      searchPayload.dateOfBirth = this.dateService.getDateAsFormattedString(formValue.dateOfBirth, 'MM/DD/YYYY');
    }

    return searchPayload;
  }

  async selectMember(member: DRXMemberSearchResponse, index: number) {
    this.showSelectSpinner[index] = true;
    this.disableOtherSelect = true;

    let memberSession = await this.drxService.createSessionFromSsoValue(member.SSOValue);

    if (!memberSession || !memberSession[0]?.SessionID) {
      console.error("No member session retrieved for selected member");
      this.showSelectSpinner[index] = false;
      this.disableOtherSelect = false;
      return;
    }

    let memberInfo = await this.drxService.getMemberInformationForSession(memberSession[0].SessionID);

    await this.applyMedicationsToSession(memberInfo.Dosages);
    this.applyPharmaciesToSession(memberInfo.Pharmacies);


    const alertDuration = 1000; // ms

    this.snackBar.open(`Saving information, will continue shortly.`, 'OK', {
      duration: alertDuration,
      panelClass: 'positive-panel-class'
    });

    setTimeout(() => {
      window.location.href = this.memberSelectedLink;
    }, alertDuration)
  }

  async applyMedicationsToSession(drugs: DRXDosageResponse[]) {
    if (!drugs?.length) {
      return;
    }

    let requestPromises = [];

    drugs.forEach(async (drug) => {
      requestPromises.push(this.loadDrugDetails(drug));
    });

    this.ngRedux.dispatch(setSelectedMedications(await Promise.all(requestPromises)));
    this.ngRedux.dispatch(storeCurrentMedications());
    this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
  }

  async loadDrugDetails(drug: DRXDosageResponse): Promise<SelectedMedication> {
    let drugDetail = await this.drugLookupService.byId(drug.DrugID);

    if (drugDetail.GenericDrugID) {
      let genericDetail = await this.drugLookupService.byId(drugDetail.GenericDrugID);

      this.ngRedux.dispatch(addMedicationToSwapMap({
        brand: drugDetail,
        generic: genericDetail
      }));
    }
    else {
      // Need to have these values defined for the drug form to work
      drugDetail.GenericDrugID = null;
      drugDetail.GenericDrugName = null;
      delete drugDetail.GenericDosages;
    }

    // drug detail is the main part of the saved object. The added parts here are the values
    // we ask for in the form on the next page.
    // Frequency is only on the UI and not available anywhere in the response
    return {
      drug: drugDetail,
      useGenericVersion: !!drugDetail.GenericDrugID,
      selectedDosage: drug.DosageID,
      selectedPackage: null,
      frequency: null,
      dosageQuantity: drug.UserQuantity
    };
  }

  applyPharmaciesToSession(pharmacies: DRXPharmacyResponse[]) {
    if (!pharmacies?.length) {
      return;
    }

    let convertedPharmacies = [];

    pharmacies.forEach((pharmacy) => {
      convertedPharmacies.push({
        name: pharmacy.Name,
        address1: pharmacy.Address1,
        address2: pharmacy.Address2,
        city: pharmacy.City,
        state: pharmacy.State,
        pharmacyId: pharmacy.PharmacyID,
        isSelected: true
      });
    });

    // default to first pharmacy as the selected one
    this.ngRedux.dispatch(setPharmacyState(convertedPharmacies, convertedPharmacies[0]));
  }

  isFormValid(): boolean {
    let form = this.form.value;

    if (form.firstName || form.lastName || form.dateOfBirth) {
      return true;
    }

    return false;
  }
}
